import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CategorySidebar from "@/components/CategorySidebar";
import NewsFeed from "@/components/NewsFeed";
import AiWorkspace from "@/components/AiWorkspace";
import TrendingTicker from "@/components/TrendingTicker";
import { Bookmark, ShieldCheck } from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("technology");
  const [interval, setIntervalMin] = useState(15);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [selected, setSelected] = useState(null);
  const [usage, setUsage] = useState(null);
  const timer = useRef(null);

  const loadUsage = useCallback(async () => {
    try {
      const { data } = await api.get("/ai/usage");
      setUsage(data);
    } catch {}
  }, []);

  const loadNews = useCallback(async (cat) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/news?category=${cat}`);
      setItems(data.items);
      setFetchedAt(data.fetched_at);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    loadUsage();
  }, [loadUsage]);

  useEffect(() => {
    loadNews(category);
    setSelected(null);
  }, [category, loadNews]);

  // Auto-refresh at chosen interval
  useEffect(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(() => loadNews(category), interval * 60 * 1000);
    return () => timer.current && clearInterval(timer.current);
  }, [interval, category, loadNews]);

  const onUpgrade = () => navigate("/subscribe");

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      {/* Top bar */}
      <header className="h-14 border-b border-border bg-white flex items-center justify-between px-5 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 border border-border rounded-full flex items-center justify-center p-0.5">
            <img src="/tejom-logo.png" alt="Tejom Digital" className="w-full h-full object-contain" />
          </div>
          <span className="font-heading font-extrabold tracking-tight">TD News Fetch</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground hidden sm:block ml-2">
            {categories.find((c) => c.id === category)?.label}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {user?.role === "admin" && (
            <button data-testid="admin-link" onClick={() => navigate("/admin")}
              className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 border border-[#111110] bg-[#111110] text-[#ffce00] hover:bg-[#ffce00] hover:text-black transition-colors">
              <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" /> Admin
            </button>
          )}
          <button data-testid="library-link" onClick={() => navigate("/library")}
            className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 border border-border hover:bg-secondary transition-colors">
            <Bookmark className="w-3.5 h-3.5" aria-hidden="true" /> Library
          </button>
          <span className="font-mono text-[10px] text-muted-foreground">Live · {interval}min refresh</span>
        </div>
      </header>

      <TrendingTicker />

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 overflow-hidden">
        <div className="hidden lg:block lg:col-span-3 xl:col-span-2 overflow-y-auto">
          <CategorySidebar
            categories={categories}
            selected={category}
            onSelect={setCategory}
            interval={interval}
            onInterval={setIntervalMin}
            onRefresh={() => loadNews(category)}
            refreshing={loading}
            fetchedAt={fetchedAt}
            user={user}
            usage={usage}
            onUpgrade={onUpgrade}
            onLogout={() => { logout(); navigate("/login"); }}
          />
        </div>

        <div className="lg:col-span-5 xl:col-span-5 overflow-y-auto border-r border-border bg-white">
          <NewsFeed items={items} loading={loading} selected={selected} onSelect={setSelected} />
        </div>

        <div className="lg:col-span-4 xl:col-span-5 overflow-hidden bg-background">
          <AiWorkspace
            article={selected}
            premium={usage?.premium}
            onUpgrade={onUpgrade}
            onUsageChange={loadUsage}
            onRewritten={() => loadNews(category)}
          />
        </div>
      </div>
    </div>
  );
}
